import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Container, Table, Form, InputGroup, Button, Row, Col, Pagination, Badge, Modal, Spinner, Alert } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import apiClient from '../../../../api/apiClient';
import distributorService from '../../../../services/DistributorService';
import { showToast } from '../../../../utils/toast';
import { beatsCache } from '../../../../utils/beatsCache';

const DistributorRetailers = () => {
  const [retailers, setRetailers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedRetailer, setSelectedRetailer] = useState(null);
  const [beats, setBeats] = useState([]);
  const [beatsLoading, setBeatsLoading] = useState(false);
  const beatsLoadedRef = useRef(false);

  // Import modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    mobile: '',
    password: '',
    customer_id: '',
    name: '',
    shop_name: '',
    email: '',
    pan_number: '',
    aadhaar_number: '',
    gst_number: '',
    region: '',
    district: '',
    city: '',
    street1: '',
    street2: '',
    street3: '',
    postal_code: '',
    latitude: '',
    longitude: '',
    status: 'active',
    beat_id: '',
    sequence_no: ''
  });
  
  const [formErrors, setFormErrors] = useState({});

  // Dropdown options
  const regions = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
    'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
    'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
    'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
    'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Delhi', 'Jammu and Kashmir', 'Ladakh'
  ];
  
  const districts = {
    'Andhra Pradesh': ['Visakhapatnam', 'Vijayawada', 'Guntur', 'Tirupati', 'Kakinada'],
    'Gujarat': ['Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Gandhinagar'],
    'Karnataka': ['Bangalore', 'Mysore', 'Mangalore', 'Hubli', 'Belgaum'],
    'Maharashtra': ['Mumbai', 'Pune', 'Nagpur', 'Nashik', 'Aurangabad'],
    'Tamil Nadu': ['Chennai', 'Coimbatore', 'Madurai', 'Tiruchirappalli', 'Salem'],
    'Telangana': ['Hyderabad', 'Warangal', 'Nizamabad', 'Karimnagar', 'Khammam'],
    'Uttar Pradesh': ['Lucknow', 'Kanpur', 'Ghaziabad', 'Agra', 'Varanasi'],
    'West Bengal': ['Kolkata', 'Howrah', 'Durgapur', 'Siliguri', 'Asansol'],
    'Delhi': ['Central Delhi', 'East Delhi', 'New Delhi', 'North Delhi', 'South Delhi'],
    'Haryana': ['Gurgaon', 'Faridabad', 'Panipat', 'Ambala', 'Yamunanagar'],
    'Punjab': ['Ludhiana', 'Amritsar', 'Jalandhar', 'Patiala', 'Bathinda'],
    'Rajasthan': ['Jaipur', 'Jodhpur', 'Udaipur', 'Kota', 'Ajmer'],
    'Kerala': ['Kochi', 'Thiruvananthapuram', 'Kozhikode', 'Thrissur', 'Kollam'],
    'Madhya Pradesh': ['Bhopal', 'Indore', 'Jabalpur', 'Gwalior', 'Ujjain'],
    'Bihar': ['Patna', 'Gaya', 'Bhagalpur', 'Muzaffarpur', 'Darbhanga'],
    'Odisha': ['Bhubaneswar', 'Cuttack', 'Rourkela', 'Brahmapur', 'Sambalpur'],
    'Assam': ['Guwahati', 'Dibrugarh', 'Jorhat', 'Silchar', 'Tezpur'],
    'Jharkhand': ['Ranchi', 'Jamshedpur', 'Dhanbad', 'Bokaro', 'Hazaribagh'],
    'Chhattisgarh': ['Raipur', 'Bhilai', 'Bilaspur', 'Korba', 'Durg'],
    'Himachal Pradesh': ['Shimla', 'Dharamshala', 'Kullu', 'Mandi', 'Solan'],
    'Uttarakhand': ['Dehradun', 'Haridwar', 'Nainital', 'Rishikesh', 'Roorkee'],
    'Goa': ['Panaji', 'Margao', 'Vasco', 'Mapusa', 'Ponda'],
    'Tripura': ['Agartala', 'Dharmanagar', 'Udaipur', 'Kailashahar', 'Belonia'],
    'Meghalaya': ['Shillong', 'Tura', 'Jowai', 'Nongstoin', 'Baghmara'],
    'Manipur': ['Imphal', 'Thoubal', 'Bishnupur', 'Churachandpur', 'Kakching'],
    'Nagaland': ['Dimapur', 'Kohima', 'Mokokchung', 'Tuensang', 'Wokha'],
    'Arunachal Pradesh': ['Itanagar', 'Naharlagun', 'Pasighat', 'Tawang', 'Ziro'],
    'Mizoram': ['Aizawl', 'Lunglei', 'Saiha', 'Champhai', 'Kolasib'],
    'Sikkim': ['Gangtok', 'Namchi', 'Gyalshing', 'Mangan', 'Rangpo'],
    'Jammu and Kashmir': ['Srinagar', 'Jammu', 'Anantnag', 'Baramulla', 'Udhampur'],
    'Ladakh': ['Leh', 'Kargil', 'Diskit', 'Padum', 'Sankoo']
  };
  
  const cities = {
    'Mumbai': ['Andheri', 'Bandra', 'Borivali', 'Dadar', 'Kurla', 'Malad', 'Kandivali', 'Goregaon'],
    'Central Delhi': ['Connaught Place', 'Karol Bagh', 'Paharganj', 'Daryaganj'],
    'East Delhi': ['Laxmi Nagar', 'Preet Vihar', 'Mayur Vihar', 'Gandhi Nagar'],
    'New Delhi': ['Chanakyapuri', 'Sarojini Nagar', 'Lajpat Nagar', 'RK Puram'],
    'North Delhi': ['Civil Lines', 'Model Town', 'Rohini', 'Pitampura'],
    'South Delhi': ['Greater Kailash', 'Saket', 'Vasant Kunj', 'Defence Colony'],
    'Bangalore': ['Koramangala', 'Whitefield', 'Electronic City', 'Indiranagar', 'Jayanagar', 'Marathahalli', 'BTM Layout', 'HSR Layout'],
    'Chennai': ['T Nagar', 'Anna Nagar', 'Velachery', 'Adyar', 'Mylapore', 'Alwarpet', 'Nungambakkam'],
    'Hyderabad': ['Gachibowli', 'Hitech City', 'Secunderabad', 'Madhapur', 'Banjara Hills', 'Jubilee Hills', 'Kukatpally'],
    'Kolkata': ['Salt Lake', 'Park Street', 'Ballygunge', 'New Town', 'Gariahat', 'Jadavpur', 'Behala'],
    'Gurgaon': ['Sector 14', 'Sector 29', 'Cyber City', 'DLF Phase 1', 'Sohna Road', 'Golf Course Road', 'Udyog Vihar'],
    'Pune': ['Hadapsar', 'Wakad', 'Kothrud', 'Hinjewadi', 'Baner', 'Aundh', 'Kalyani Nagar'],
    'Ahmedabad': ['Satellite', 'Vastrapur', 'Bopal', 'Maninagar', 'Navrangpura', 'SG Highway', 'Bodakdev'],
    'Surat': ['Adajan', 'Athwa', 'Rander', 'Vesu', 'City Light', 'Katargam', 'Varachha'],
    'Lucknow': ['Gomti Nagar', 'Hazratganj', 'Aliganj', 'Indira Nagar', 'Aminabad'],
    'Jaipur': ['Malviya Nagar', 'Vaishali Nagar', 'C-Scheme', 'Raja Park', 'Mansarovar'],
    'Bhopal': ['New Market', 'MP Nagar', 'Arera Colony', '10 Number Market', 'Kolar Road'],
    'Indore': ['Vijay Nagar', 'Palasia', 'Rajwada', 'Sapna Sangeeta', 'Old Palasia'],
    'Patna': ['Boring Road', 'Kankarbagh', 'Rajendra Nagar', 'Patliputra', 'Ashok Rajpath'],
    'Kanpur': ['Swaroop Nagar', 'Kakadeo', 'Civil Lines', 'Mall Road', 'Kidwai Nagar'],
    'Nagpur': ['Sitabuldi', 'Dharampeth', 'Sadar', 'Ramdaspeth', 'Civil Lines'],
    'Visakhapatnam': ['Dwaraka Nagar', 'Gajuwaka', 'MVP Colony', 'Maddilapalem', 'Beach Road'],
    'Bhubaneswar': ['Khandagiri', 'Jaydev Vihar', 'Sahid Nagar', 'Patia', 'Chandrasekharpur'],
    'Coimbatore': ['RS Puram', 'Gandhipuram', 'Saibaba Colony', 'Peelamedu', 'Race Course'],
    'Kochi': ['Ernakulam', 'Fort Kochi', 'Kakkanad', 'Marine Drive', 'Palarivattom'],
    'Guwahati': ['Fancy Bazar', 'Paltan Bazar', 'Zoo Road', 'GS Road', 'Beltola']
  };


  // Fetch Retailer users from API
  const fetchRetailerUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        page_size: rowsPerPage
      };
      
      if (searchQuery) params.search = searchQuery;
      
      const response = await apiClient.get('/api/distributor/retailer/', { params });
      
      if (response.data) {
        setRetailers(response.data.results || []);
        setTotalCount(response.data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching retailer users:', error);
      showToast.error('Failed to fetch retailer users');
    } finally {
      setLoading(false);
    }
  }, [currentPage, rowsPerPage, searchQuery]);

  // Fetch Beats from API using cache
  const fetchBeats = useCallback(async () => {
    if (beatsLoadedRef.current) {
      return; // Skip if already loaded locally
    }
    
    setBeatsLoading(true);
    try {
      const response = await beatsCache.getBeats(async () => {
        return await distributorService.getBeats({ page_size: 100 });
      });
      
      if (response.success) {
        setBeats(response.data.results || []);
        beatsLoadedRef.current = true; // Mark as loaded locally
      } else {
        showToast.error(response.error || 'Failed to fetch beats');
      }
    } catch (error) {
      console.error('Error fetching beats:', error);
      showToast.error('Failed to fetch beats');
    } finally {
      setBeatsLoading(false);
    }
  }, []);

  // Debounced effect for retailer users
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchRetailerUsers();
    }, searchQuery ? 300 : 0);

    return () => clearTimeout(timer);
  }, [fetchRetailerUsers]);

  // Effect for beats (only on mount)
  useEffect(() => {
    fetchBeats();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPages = Math.ceil(totalCount / rowsPerPage);
  
  const handleSearch = (query) => {
    setSearchQuery(query);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };
  

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(retailers);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Retailers');
    XLSX.writeFile(workbook, `retailers_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleAdd = () => {
    setSelectedRetailer(null);
    setModalMode('add');
    setFormData({
      mobile: '',
      password: '',
      customer_id: '',
      name: '',
      shop_name: '',
      email: '',
      pan_number: '',
      aadhaar_number: '',
      gst_number: '',
      region: '',
      district: '',
      city: '',
      street1: '',
      street2: '',
      street3: '',
      postal_code: '',
      latitude: '',
      longitude: '',
      status: 'active',
      beat_id: '',
      sequence_no: ''
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (retailer) => {
    setSelectedRetailer(retailer);
    setModalMode('edit');
    setFormData({
      mobile: retailer.mobile,
      password: '', // Don't populate password for edit
      customer_id: retailer.customer_id || '',
      name: retailer.name,
      shop_name: retailer.shop_name || '',
      email: retailer.email || '',
      pan_number: retailer.pan_number || '',
      aadhaar_number: retailer.aadhaar_number || '',
      gst_number: retailer.gst_number || '',
      region: retailer.region || '',
      district: retailer.district || '',
      city: retailer.city || '',
      street1: '', // These would come from a detailed API call
      street2: '',
      street3: '',
      postal_code: '',
      latitude: retailer.latitude || '',
      longitude: retailer.longitude || '',
      status: retailer.status,
      beat_id: retailer.retailer_beat_id || '',
      sequence_no: retailer.retailer_sequence_no || ''
    });
    setFormErrors({});
    setShowModal(true);
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormErrors({});
    
    try {
      const payload = {
        ...formData,
        role: 'retailer'
      };
      
      // Remove empty password for edit mode
      if (modalMode === 'edit' && !payload.password) {
        delete payload.password;
      }
      
      // Convert beat_id to integer if provided
      if (payload.beat_id) {
        payload.beat_id = parseInt(payload.beat_id);
      } else {
        payload.beat_id = null;
      }
      
      // Convert sequence_no to integer if provided
      if (payload.sequence_no) {
        payload.sequence_no = parseInt(payload.sequence_no);
      } else {
        payload.sequence_no = null;
      }
      
      let response;
      if (modalMode === 'add') {
        response = await apiClient.post('/api/distributor/users/', payload);
        showToast.success('Retailer created successfully');
      } else {
        response = await apiClient.put(`/api/distributor/users/${selectedRetailer.id}/`, payload);
        showToast.success('Retailer updated successfully');
      }
      
      setShowModal(false);
      fetchRetailerUsers();
    } catch (error) {
      console.error('Error saving retailer:', error);
      
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      } else if (error.response?.data?.message) {
        showToast.error(error.response.data.message);
      } else {
        showToast.error(`Failed to ${modalMode === 'add' ? 'create' : 'update'} retailer`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: value
      };

      // Reset dependent fields when parent changes
      if (field === 'region') {
        updated.district = '';
        updated.city = '';
      } else if (field === 'district') {
        updated.city = '';
      }

      return updated;
    });
  };

  // Download import template
  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const response = await distributorService.downloadRetailerImportTemplate();

      if (response.success) {
        // Create a blob URL and trigger download
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.fileName || 'retailer_import_template.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        showToast.success('Template downloaded successfully');
      } else {
        showToast.error(response.error || 'Failed to download template');
      }
    } catch (error) {
      console.error('Error downloading template:', error);
      showToast.error('Failed to download template');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'text/csv'
      ];
      const validExtensions = ['.xlsx', '.xls', '.csv'];
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

      if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
        showToast.error('Please select a valid Excel or CSV file');
        return;
      }

      setImportFile(file);
    }
  };

  // Handle import
  const handleImport = async () => {
    if (!importFile) {
      showToast.error('Please select a file to import');
      return;
    }

    setImporting(true);
    try {
      const response = await distributorService.importRetailers(importFile);

      if (response.success) {
        showToast.success(response.message || 'Retailers imported successfully');
        setShowImportModal(false);
        setImportFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        // Refresh the retailers list
        fetchRetailerUsers();
      } else {
        showToast.error(response.error || 'Failed to import retailers');
      }
    } catch (error) {
      console.error('Error importing retailers:', error);
      showToast.error('Failed to import retailers');
    } finally {
      setImporting(false);
    }
  };

  // Open import modal
  const handleOpenImportModal = () => {
    setImportFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setShowImportModal(true);
  };

  const getStatusBadge = (status) => {
    return status === 'active' ? 
      <Badge bg="success">Active</Badge> : 
      <Badge bg="danger">Inactive</Badge>;
  };
  
  const getKycBadge = (kyc) => {
    if (kyc === 'approved') return <Badge bg="success">Approved</Badge>;
    if (kyc === 'pending') return <Badge bg="warning">Pending</Badge>;
    if (kyc === 'rejected') return <Badge bg="danger">Rejected</Badge>;
    return <Badge bg="secondary">Not Submitted</Badge>;
  };

  return (
    <Container fluid className="py-1">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 style={{ color: '#031a6f', fontWeight: '600' }}>
          <i className="fas fa-store me-2"></i>
          Retailers
        </h5>
        <div className="d-flex gap-2">
          <Button
            variant="outline-info"
            onClick={handleDownloadTemplate}
            disabled={downloadingTemplate}
          >
            {downloadingTemplate ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Downloading...
              </>
            ) : (
              <>
                <i className="fas fa-download me-2"></i>
                Download Sample File
              </>
            )}
          </Button>
          <Button variant="info" onClick={handleOpenImportModal}>
            <i className="fas fa-file-import me-2"></i>
            Import
          </Button>
          <Button variant="success" onClick={exportToExcel}>
            <i className="fas fa-file-excel me-2"></i>
            Export Excel
          </Button>
          <Button variant="primary" onClick={handleAdd}>
            <i className="fas fa-plus me-2"></i>
            Add Retailer
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Row className="mb-4">
        <Col md={6}>
          <InputGroup>
            <InputGroup.Text>
              <i className="fas fa-search"></i>
            </InputGroup.Text>
            <Form.Control
              placeholder="Search retailers..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </InputGroup>
        </Col>
      </Row>

      {/* Table */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <div className="table-responsive">
          <Table striped bordered hover className="modern-table">
            <thead>
              <tr>
                <th>Actions</th>
                <th>PRM ID</th>
                <th>Store/Owner Info</th>
                <th>Contact</th>
                <th>Location</th>
                <th>Beat/Sequence</th>
                <th>Outstanding</th>
                <th>KYC</th>
                <th>Status</th>
                <th>Last Login</th>
              </tr>
            </thead>
            <tbody>
              {retailers.map((retailer) => (
                <tr key={retailer.id}>
                  <td>
                    <div className="d-flex gap-1">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleEdit(retailer)}
                        title="Edit Retailer"
                      >
                        <i className="fas fa-edit"></i>
                      </Button>
                    </div>
                  </td>
                  <td><strong>{retailer.customer_id}</strong></td>
                  <td>
                    <div>
                      <div><strong>{retailer.shop_name || 'N/A'}</strong></div>
                      <small className="text-muted">{retailer.name}</small>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div>{retailer.mobile}</div>
                      <small className="text-muted">{retailer.email}</small>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div>{retailer.city}, {retailer.district}</div>
                      <small className="text-muted">{retailer.region}</small>
                    </div>
                  </td>
                  <td>
                    <div>
                      {retailer.retailer_beat_name ? (
                        <>
                          <div className="fw-bold">{retailer.retailer_beat_name}</div>
                          {retailer.retailer_sequence_no && (
                            <small className="text-muted">Seq: {retailer.retailer_sequence_no}</small>
                          )}
                        </>
                      ) : (
                        <small className="text-muted">No Beat Assigned</small>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className={retailer.total_outstanding > 0 ? 'text-danger fw-bold' : 'text-success'}>
                      ₹{retailer.total_outstanding?.toLocaleString() || '0'}
                    </div>
                    {retailer.total_outstanding > 0 && (
                      <small className="text-danger">Overdue</small>
                    )}
                  </td>
                  <td>{getKycBadge(retailer.kyc)}</td>
                  <td>{getStatusBadge(retailer.status)}</td>
                  <td>
                    <small className="text-muted">
                      {retailer.last_login_display || 'Never'}
                    </small>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          
          {retailers.length === 0 && (
            <div className="text-center py-4">
              <p className="text-muted">No retailers found</p>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div>
          <Form.Select
            value={rowsPerPage}
            onChange={(e) => {
              const newRowsPerPage = Number(e.target.value);
              setRowsPerPage(newRowsPerPage);
              setCurrentPage(1);
            }}
            style={{ width: 'auto' }}
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
          </Form.Select>
        </div>
        <div>
          Showing {((currentPage - 1) * rowsPerPage) + 1} to {Math.min(currentPage * rowsPerPage, totalCount)} of {totalCount} entries
        </div>
        <Pagination>
          <Pagination.First 
            onClick={() => setCurrentPage(1)} 
            disabled={currentPage === 1} 
          />
          <Pagination.Prev 
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
            disabled={currentPage === 1} 
          />
          {(() => {
            const pages = [];
            const maxVisiblePages = 5;
            
            let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
            let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
            
            if (endPage - startPage < maxVisiblePages - 1) {
              startPage = Math.max(1, endPage - maxVisiblePages + 1);
            }
            
            for (let i = startPage; i <= endPage; i++) {
              pages.push(
                <Pagination.Item
                  key={i}
                  active={i === currentPage}
                  onClick={() => setCurrentPage(i)}
                >
                  {i}
                </Pagination.Item>
              );
            }
            
            return pages;
          })()}
          <Pagination.Next 
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
            disabled={currentPage === totalPages} 
          />
          <Pagination.Last 
            onClick={() => setCurrentPage(totalPages)} 
            disabled={currentPage === totalPages} 
          />
        </Pagination>
      </div>

      {/* Retailer Form Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>{modalMode === 'add' ? 'Add New' : 'Edit'} Retailer</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {Object.keys(formErrors).length > 0 && (
              <Alert variant="danger">
                <ul className="mb-0">
                  {Object.entries(formErrors).map(([field, errors]) => (
                    <li key={field}>{field}: {Array.isArray(errors) ? errors.join(', ') : errors}</li>
                  ))}
                </ul>
              </Alert>
            )}
            
            <Row>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Mobile Number *</Form.Label>
                  <Form.Control
                    type="tel"
                    value={formData.mobile}
                    onChange={(e) => handleInputChange('mobile', e.target.value)}
                    placeholder="9876543210"
                    maxLength={10}
                    disabled={modalMode === 'edit'}
                    isInvalid={formErrors.mobile}
                    required
                  />
                </Form.Group>
              </Col>
              
              {modalMode === 'add' && (
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>Password *</Form.Label>
                    <Form.Control
                      type="password"
                      autoComplete="off"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      placeholder="Enter password"
                      isInvalid={formErrors.password}
                      required
                    />
                  </Form.Group>
                </Col>
              )}
              
              {modalMode === 'edit' && (
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>New Password (optional)</Form.Label>
                    <Form.Control
                      type="password"
                      autoComplete="off"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      placeholder="Enter new password to change"
                    />
                  </Form.Group>
                </Col>
              )}
              
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>PRM ID</Form.Label>
                  <Form.Control
                    type="text"
                    autoComplete="off"
                    value={formData.customer_id}
                    onChange={(e) => handleInputChange('customer_id', e.target.value)}
                    placeholder="CUST001"
                    isInvalid={formErrors.customer_id}
                  />
                </Form.Group>
              </Col>
              
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Owner Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter owner name"
                    isInvalid={formErrors.name}
                    required
                  />
                </Form.Group>
              </Col>
              
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Shop Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.shop_name}
                    onChange={(e) => handleInputChange('shop_name', e.target.value)}
                    placeholder="Enter shop name"
                    isInvalid={formErrors.shop_name}
                  />
                </Form.Group>
              </Col>
              
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Enter email address"
                    isInvalid={formErrors.email}
                  />
                </Form.Group>
              </Col>
              
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>PAN Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.pan_number}
                    onChange={(e) => handleInputChange('pan_number', e.target.value.toUpperCase())}
                    placeholder="ABCDE1234F"
                    maxLength={10}
                    isInvalid={formErrors.pan_number}
                  />
                </Form.Group>
              </Col>
              
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Aadhaar Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.aadhaar_number}
                    onChange={(e) => handleInputChange('aadhaar_number', e.target.value)}
                    placeholder="123456789012"
                    maxLength={12}
                    isInvalid={formErrors.aadhaar_number}
                  />
                </Form.Group>
              </Col>
              
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>GST Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.gst_number}
                    onChange={(e) => handleInputChange('gst_number', e.target.value.toUpperCase())}
                    placeholder="27AACCW9457C1Z1"
                    maxLength={15}
                    isInvalid={formErrors.gst_number}
                  />
                </Form.Group>
              </Col>
              
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Region</Form.Label>
                  <Form.Select
                    value={formData.region}
                    onChange={(e) => handleInputChange('region', e.target.value)}
                    isInvalid={formErrors.region}
                  >
                    <option value="">Select Region</option>
                    {regions.map(region => (
                      <option key={region} value={region}>{region}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>District</Form.Label>
                  <Form.Select
                    value={formData.district}
                    onChange={(e) => handleInputChange('district', e.target.value)}
                    disabled={!formData.region}
                    isInvalid={formErrors.district}
                  >
                    <option value="">Select District</option>
                    {formData.region && districts[formData.region]?.map(district => (
                      <option key={district} value={district}>{district}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>City</Form.Label>
                  <Form.Select
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    disabled={!formData.district}
                    isInvalid={formErrors.city}
                  >
                    <option value="">Select City</option>
                    {formData.district && cities[formData.district]?.map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Street Address 1</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.street1}
                    onChange={(e) => handleInputChange('street1', e.target.value)}
                    placeholder="123 Main Street"
                  />
                </Form.Group>
              </Col>
              
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Street Address 2</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.street2}
                    onChange={(e) => handleInputChange('street2', e.target.value)}
                    placeholder="Near City Mall"
                  />
                </Form.Group>
              </Col>
              
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Postal Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.postal_code}
                    onChange={(e) => handleInputChange('postal_code', e.target.value)}
                    placeholder="110001"
                    maxLength={6}
                  />
                </Form.Group>
              </Col>
              
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Latitude</Form.Label>
                  <Form.Control
                    type="number"
                    step="any"
                    value={formData.latitude}
                    onChange={(e) => handleInputChange('latitude', e.target.value)}
                    placeholder="28.7041"
                    isInvalid={formErrors.latitude}
                  />
                </Form.Group>
              </Col>
              
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Longitude</Form.Label>
                  <Form.Control
                    type="number"
                    step="any"
                    value={formData.longitude}
                    onChange={(e) => handleInputChange('longitude', e.target.value)}
                    placeholder="77.1025"
                    isInvalid={formErrors.longitude}
                  />
                </Form.Group>
              </Col>
              
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Beat</Form.Label>
                  <Form.Select
                    value={formData.beat_id}
                    onChange={(e) => handleInputChange('beat_id', e.target.value)}
                    disabled={beatsLoading}
                  >
                    <option value="">Select Beat</option>
                    {beats.map((beat) => (
                      <option key={beat.id} value={beat.id}>
                        {beat.beat_name}
                      </option>
                    ))}
                  </Form.Select>
                  {beatsLoading && (
                    <Form.Text className="text-muted">
                      <Spinner animation="border" size="sm" className="me-2" />
                      Loading beats...
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
              
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Sequence Number</Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.sequence_no}
                    onChange={(e) => handleInputChange('sequence_no', e.target.value)}
                    placeholder="Enter sequence number"
                    min="1"
                  />
                  <Form.Text className="text-muted">
                    Order sequence for this retailer in the beat
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    required
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {modalMode === 'add' ? 'Creating...' : 'Updating...'}
                </>
              ) : (
                <>
                  {modalMode === 'add' ? 'Create' : 'Update'} Retailer
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Import Retailers Modal */}
      <Modal show={showImportModal} onHide={() => setShowImportModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Import Retailers</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <p className="text-muted mb-3">
              Upload an Excel file (.xlsx, .xls) or CSV file to import retailers in bulk.
              <br />
              <small>
                <Button
                  variant="link"
                  className="p-0"
                  onClick={handleDownloadTemplate}
                  disabled={downloadingTemplate}
                >
                  {downloadingTemplate ? 'Downloading...' : 'Download sample file'}
                </Button>
                {' '}to see the required format.
              </small>
            </p>

            <Form.Group>
              <Form.Label>Select File *</Form.Label>
              <Form.Control
                type="file"
                ref={fileInputRef}
                accept=".xlsx,.xls,.csv"
                onChange={handleFileSelect}
                disabled={importing}
              />
              <Form.Text className="text-muted">
                Supported formats: .xlsx, .xls, .csv
              </Form.Text>
            </Form.Group>

            {importFile && (
              <Alert variant="info" className="mt-3 mb-0">
                <i className="fas fa-file-excel me-2"></i>
                Selected file: <strong>{importFile.name}</strong>
                <br />
                <small>Size: {(importFile.size / 1024).toFixed(2)} KB</small>
              </Alert>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowImportModal(false)}
            disabled={importing}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleImport}
            disabled={importing || !importFile}
          >
            {importing ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Importing...
              </>
            ) : (
              <>
                <i className="fas fa-upload me-2"></i>
                Import
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
};

export default DistributorRetailers;